import { useState, useCallback } from "react";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { toast } from "sonner";
import {
  Loader2, X, Info, AlertTriangle, CheckCircle2,
  Microscope, Shield, Target,
} from "lucide-react";
import { CollapsibleSection } from "./CollapsibleSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getStage2Narrative(stage2) {
  const r2 = stage2.summary?.adj_r_squared ?? stage2.summary?.pseudo_r_squared ?? stage2.summary?.r_squared;
  if (r2 == null) return null;
  if (r2 < 0.10 || r2 < 0) return "Model explanatory power is weak.";
  if (r2 >= 0.50) return "Model demonstrates strong explanatory power.";
  if (r2 >= 0.30) return "Model demonstrates moderate explanatory power.";
  return "Model explanatory power is weak.";
}

function deriveRcaSummary(result) {
  const confidence = result.confidence || {};
  const stage2 = result.stage2 || {};
  const stage1 = result.stage1 || {};
  const guardrails = result.guardrails || {};
  const mp = guardrails.measure_preconditions || {};
  const stability = mp.stability_status || "unknown";
  const score = confidence.score ?? 0;
  const drivers = result.final_drivers || [];
  const validatedDrivers = drivers.filter(d => d.validation_status === "validated");
  const dvType = result.dv_type;

  let rcStatus, rcColor;
  if (dvType === "time_index") {
    rcStatus = "Invalid Outcome Variable \u2014 Time Index Not Eligible";
    rcColor = "text-red-400";
  } else if (validatedDrivers.length > 0 && score >= 80 && stability === "stable") {
    rcStatus = "Validated Root Cause Identified";
    rcColor = "text-emerald-400";
  } else if (stage2.ran && stage2.predictors?.length > 0 && stability === "unstable") {
    rcStatus = "Association Observed \u2014 Process Unstable";
    rcColor = "text-amber-400";
  } else if ((stage1.validated_predictors?.length > 0) && (score < 80 || !stage2.ran || (stage2.ran && validatedDrivers.length === 0))) {
    rcStatus = "Preliminary Association \u2014 Further Validation Required";
    rcColor = "text-amber-400";
  } else {
    rcStatus = "No Statistically Validated Drivers Identified";
    rcColor = "text-red-400";
  }

  let modelStrength, modelColor;
  if (score >= 80) { modelStrength = "Strong"; modelColor = "text-emerald-400"; }
  else if (score >= 55) { modelStrength = "Moderate"; modelColor = "text-amber-400"; }
  else { modelStrength = "Weak"; modelColor = "text-red-400"; }

  let stabilityLabel, stabilityColor;
  if (stability === "unstable") { stabilityLabel = "Unstable \u2014 Inference Downgraded"; stabilityColor = "text-red-400"; }
  else if (stability === "stable") { stabilityLabel = "Stable"; stabilityColor = "text-emerald-400"; }
  else { stabilityLabel = "Unknown \u2014 Pilot Required"; stabilityColor = "text-amber-400"; }

  let nextAction;
  if (dvType === "time_index") {
    nextAction = "Select an eligible outcome variable";
  } else if (stability === "unstable") {
    nextAction = "Restore process stability before implementing changes";
  } else if (validatedDrivers.length === 0 && (stage1.validated_predictors?.length || 0) === 0) {
    nextAction = "Collect additional data or expand factor measurement";
  } else if (score < 55) {
    nextAction = "Model explanatory power insufficient \u2014 collect additional data";
  } else if (validatedDrivers.length > 0 && score >= 80 && stability === "stable") {
    nextAction = "Proceed to Improve (Pilot Required per Governance)";
  } else {
    nextAction = "Address identified weaknesses before proceeding";
  }

  return { rcStatus, rcColor, modelStrength, modelColor, stabilityLabel, stabilityColor, nextAction };
}

function RcaExecutiveSummary({ result }) {
  const s = deriveRcaSummary(result);
  return (
    <div className="bg-card border-2 border-primary/20 rounded-lg p-5" data-testid="rca-executive-summary">
      <h4 className="text-xs font-bold uppercase tracking-wider text-primary mb-4">Analysis Summary</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
        <div>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Root Cause Status</p>
          <p className={`text-sm font-semibold ${s.rcColor}`} data-testid="exec-rc-status">{s.rcStatus}</p>
        </div>
        <div>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Model Strength</p>
          <p className={`text-sm font-semibold ${s.modelColor}`} data-testid="exec-model-strength">{s.modelStrength}</p>
        </div>
        <div>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Process Stability</p>
          <p className={`text-sm font-semibold ${s.stabilityColor}`} data-testid="exec-stability">{s.stabilityLabel}</p>
        </div>
        <div>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Recommended Next Action</p>
          <p className="text-sm font-semibold text-foreground" data-testid="exec-next-action">{s.nextAction}</p>
        </div>
      </div>
    </div>
  );
}

function FinalDriversPanel({ result }) {
  const guardrails = result.guardrails || {};
  const mp = guardrails.measure_preconditions || {};
  const isUnstable = mp.stability_status === "unstable";
  const drivers = result.final_drivers || [];

  return (
    <div className="bg-card border border-border rounded-lg p-6" data-testid="rca-ranked-drivers">
      <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
        <Target className="w-4 h-4 text-primary" />
        Final Ranked Drivers
        {isUnstable && (
          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-400 ml-2" data-testid="unstable-drivers-badge">
            Preliminary \u2014 Unstable Process
          </span>
        )}
      </h4>
      {drivers.filter(d => d.validation_status === "validated" || d.validation_status === "observed_unstable").length > 0 && (
        <div className="space-y-2 mb-4">
          {drivers.filter(d => d.validation_status === "validated" || d.validation_status === "observed_unstable").map((d, i) => (
            <div key={i} className={`flex items-center justify-between p-3 rounded-lg ${
              isUnstable ? "bg-amber-500/5 border border-dashed border-amber-500/30" : "bg-background border border-border"
            }`} data-testid={`ranked-driver-${d.final_rank}`}>
              <div className="flex items-center gap-3">
                <span className={`text-lg font-bold w-8 text-center ${isUnstable ? "text-amber-400" : "text-primary"}`}>#{d.final_rank}</span>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">{d.variable}</p>
                    {isUnstable && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 font-medium">Preliminary</span>
                    )}
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    Std coef = {d.standardized_coef?.toFixed(4)}
                    {d.p_value != null && ` | p = ${d.p_value.toFixed(4)}`}
                    {d.effect_size?.reliable && d.effect_size?.magnitude && (
                      <span className="ml-1 font-medium">({d.effect_size.magnitude})</span>
                    )}
                  </p>
                  {d.validation_status === "observed_unstable" && (
                    <p className="text-[9px] text-amber-400 italic mt-0.5">Association observed under unstable conditions</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {d.direction && (
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                    d.direction === "positive" ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"
                  }`}>{d.direction}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {drivers.filter(d => d.validation_status === "observed_not_validated").length > 0 && (
        <div className="mt-3 pt-3 border-t border-dashed border-border">
          <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider mb-2">Observed associations (not statistically validated)</p>
          <div className="space-y-2">
            {drivers.filter(d => d.validation_status === "observed_not_validated").map((d, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-muted/20 border border-dashed border-border/50 rounded-lg opacity-70" data-testid={`observed-driver-${d.variable}`}>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground w-8 text-center">~</span>
                  <div>
                    <p className="text-sm text-muted-foreground">{d.variable}</p>
                    <p className="text-[10px] text-muted-foreground italic">
                      {d.screening_method} | p = {d.screening_p?.toFixed(4)} — effect not statistically validated
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function StagedRcaTab({ projectId, token, datasets, selectedDataset, setSelectedDataset, onRcaResult }) {
  const [rcaTarget, setRcaTarget] = useState("");
  const [rcaLoading, setRcaLoading] = useState(false);
  const [rcaResult, setRcaResult] = useState(null);
  const [debugClassifications, setDebugClassifications] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [classificationsOpen, setClassificationsOpen] = useState(false);
  const [stage1Open, setStage1Open] = useState(false);
  const [stage2Open, setStage2Open] = useState(false);
  const [confidenceOpen, setConfidenceOpen] = useState(false);

  const runStagedRCA = useCallback(async () => {
    if (!selectedDataset || !rcaTarget) {
      toast.error("Select a dataset and target variable");
      return;
    }
    setRcaLoading(true);
    setRcaResult(null);
    try {
      const res = await fetch(
        `${API}/projects/${projectId}/datasets/${selectedDataset.dataset_id}/staged-rca`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ target_column: rcaTarget, alpha: 0.05 }),
        }
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(typeof err.detail === "string" ? err.detail : "Analysis failed");
      }
      const data = await res.json();
      setRcaResult(data);
      onRcaResult?.(data);
      toast.success("Staged RCA complete");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setRcaLoading(false);
    }
  }, [projectId, token, selectedDataset, rcaTarget, onRcaResult]);

  const runClassifyVariables = useCallback(async () => {
    if (!selectedDataset) {
      toast.error("Select a dataset first");
      return;
    }
    setDebugLoading(true);
    try {
      const res = await fetch(
        `${API}/projects/${projectId}/datasets/${selectedDataset.dataset_id}/classify-variables`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Classification failed");
      const data = await res.json();
      setDebugClassifications(data);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDebugLoading(false);
    }
  }, [projectId, token, selectedDataset]);

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-heading font-semibold flex items-center gap-2">
            <Microscope className="w-5 h-5 text-primary" />
            Staged Root Cause Engine
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Deterministic statistical screening followed by multivariate modeling.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-4">
        <div className="space-y-2">
          <Label>Dataset</Label>
          <select
            value={selectedDataset?.dataset_id || ""}
            onChange={(e) => {
              const ds = datasets.find((d) => d.dataset_id === e.target.value);
              setSelectedDataset(ds);
              setRcaTarget("");
              setRcaResult(null);
              setDebugClassifications(null);
            }}
            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            data-testid="rca-dataset-select"
          >
            <option value="">Select dataset</option>
            {datasets.map((ds) => (
              <option key={ds.dataset_id} value={ds.dataset_id}>{ds.filename}</option>
            ))}
          </select>
        </div>
        <div className="space-y-2">
          <Label>Target Variable (DV)</Label>
          <select
            value={rcaTarget}
            onChange={(e) => { setRcaTarget(e.target.value); setRcaResult(null); }}
            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            disabled={!selectedDataset}
            data-testid="rca-target-select"
          >
            <option value="">Select target column</option>
            {selectedDataset?.columns?.map((col) => (
              <option key={col} value={col}>{col}</option>
            ))}
          </select>
        </div>
        <div className="flex items-end gap-2">
          <Button onClick={runStagedRCA} disabled={!selectedDataset || !rcaTarget || rcaLoading} className="flex-1 gap-1.5" data-testid="run-staged-rca-btn">
            {rcaLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Microscope className="w-4 h-4" />}
            Run Staged Analysis
          </Button>
          <Button variant="outline" size="icon" onClick={runClassifyVariables} disabled={!selectedDataset || debugLoading} title="Classify Variables (Debug)" data-testid="classify-variables-btn">
            {debugLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Info className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Debug Panel */}
      {debugClassifications && (
        <div className="mb-4 p-4 bg-muted/30 border border-dashed border-border rounded-lg" data-testid="debug-classification-panel">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <Info className="w-3.5 h-3.5" /> Type Classification Diagnostic
            </h4>
            <Button variant="ghost" size="sm" onClick={() => setDebugClassifications(null)} className="h-6 w-6 p-0" data-testid="close-debug-panel">
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="debug-classification-table">
              <thead>
                <tr className="border-b border-border text-muted-foreground">
                  <th className="text-left py-1.5 px-3 font-medium">Column Name</th>
                  <th className="text-left py-1.5 px-3 font-medium">Classified Type</th>
                  <th className="text-left py-1.5 px-3 font-medium">Raw dtype</th>
                  <th className="text-right py-1.5 px-3 font-medium">Unique</th>
                  <th className="text-right py-1.5 px-3 font-medium">Non-null</th>
                </tr>
              </thead>
              <tbody>
                {debugClassifications.details.map((d, i) => (
                  <tr key={i} className="border-b border-border/30">
                    <td className="py-1.5 px-3 font-medium">{d.column}</td>
                    <td className="py-1.5 px-3">
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${
                        d.classified_type === "time_index" ? "bg-blue-500/10 border-blue-500/30 text-blue-400" :
                        d.classified_type === "continuous" ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400" :
                        d.classified_type === "count" ? "bg-amber-500/10 border-amber-500/30 text-amber-400" :
                        d.classified_type === "binary" ? "bg-purple-500/10 border-purple-500/30 text-purple-400" :
                        d.classified_type === "derived_metric" ? "bg-primary/10 border-primary/30 text-primary" :
                        "bg-muted border-border text-muted-foreground"
                      }`}>{d.classified_type}</span>
                    </td>
                    <td className="py-1.5 px-3 text-muted-foreground font-mono">{d.dtype}</td>
                    <td className="py-1.5 px-3 text-right text-muted-foreground">{d.unique_count}</td>
                    <td className="py-1.5 px-3 text-right text-muted-foreground">{d.non_null_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* RCA Results */}
      {rcaResult && (
        <div className="space-y-4">
          <RcaExecutiveSummary result={rcaResult} />

          {rcaResult.classifications && (
            <CollapsibleSection title="Variable Classifications" open={classificationsOpen} onToggle={() => setClassificationsOpen(v => !v)} testId="rca-classifications">
              <div className="flex flex-wrap gap-2">
                {Object.entries(rcaResult.classifications).map(([col, type]) => (
                  <span key={col} className={`text-[10px] font-medium px-2.5 py-1 rounded-full border ${
                    type === "time_index" ? "bg-blue-500/10 border-blue-500/30 text-blue-400" :
                    type === "continuous" ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400" :
                    type === "count" ? "bg-amber-500/10 border-amber-500/30 text-amber-400" :
                    type === "binary" ? "bg-purple-500/10 border-purple-500/30 text-purple-400" :
                    type === "derived_metric" ? "bg-primary/10 border-primary/30 text-primary" :
                    "bg-muted border-border text-muted-foreground"
                  }`}>{col}: {type}</span>
                ))}
              </div>
            </CollapsibleSection>
          )}

          {rcaResult.stage1 && (
            <CollapsibleSection
              title={<><span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/15 text-primary mr-2">STAGE 1</span>Univariate Screening</>}
              subtitle={rcaResult.stage1.blocked ? null : `${rcaResult.stage1.validated_predictors?.length || 0} predictor(s) passed to Stage 2`}
              open={stage1Open} onToggle={() => setStage1Open(v => !v)} testId="rca-stage1"
            >
              <p className="text-xs text-muted-foreground mb-4">
                Target: <span className="font-medium text-foreground">{rcaResult.stage1.target}</span>
                {" "}({rcaResult.stage1.target_type})
                {rcaResult.stage1.alpha && ` | \u03B1 = ${rcaResult.stage1.alpha}`}
              </p>
              {rcaResult.stage1.blocked ? (
                <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                  <p className="text-sm text-red-400">{rcaResult.stage1.reason}</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="screening-table">
                      <thead>
                        <tr className="border-b border-border text-muted-foreground">
                          <th className="text-left py-2 px-3 font-medium">Variable</th>
                          <th className="text-left py-2 px-3 font-medium">Type</th>
                          <th className="text-left py-2 px-3 font-medium">Method</th>
                          <th className="text-right py-2 px-3 font-medium">p-value</th>
                          <th className="text-right py-2 px-3 font-medium">Effect Size</th>
                          <th className="text-left py-2 px-3 font-medium">Magnitude</th>
                          <th className="text-center py-2 px-3 font-medium">Assumptions</th>
                          <th className="text-center py-2 px-3 font-medium">Stage 2</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rcaResult.stage1.results.map((r, i) => (
                          <tr key={i} className={`border-b border-border/50 ${r.passed ? "bg-emerald-500/5" : ""}`}>
                            <td className="py-2 px-3 font-medium">{r.iv}</td>
                            <td className="py-2 px-3 text-muted-foreground">{r.iv_type}</td>
                            <td className="py-2 px-3" title={r.method_explanation || ""}>
                              <span>{r.method_label || r.method || "-"}</span>
                              {r.method_explanation && <span className="block text-[9px] text-muted-foreground mt-0.5">{r.method_explanation}</span>}
                            </td>
                            <td className={`py-2 px-3 text-right font-mono ${r.significant ? "text-emerald-400 font-medium" : "text-muted-foreground"}`}>
                              {r.p_value != null ? r.p_value.toFixed(4) : "-"}
                            </td>
                            <td className="py-2 px-3 text-right font-mono">
                              {r.effect_size?.value != null ? (
                                <>{r.effect_size.value.toFixed(4)}{r.effect_size.display_name && <span className="text-muted-foreground ml-1">({r.effect_size.display_name})</span>}</>
                              ) : "-"}
                            </td>
                            <td className="py-2 px-3">
                              {r.effect_size?.reliable === false ? (
                                <span className="text-[10px] text-muted-foreground italic cursor-help" title={r.effect_size?.reliability_reason || ""} data-testid={`effect-unreliable-${r.iv}`}>Not statistically reliable</span>
                              ) : r.effect_size?.magnitude ? (
                                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                                  r.effect_size.magnitude === "large" ? "bg-emerald-500/15 text-emerald-400" :
                                  r.effect_size.magnitude === "medium" ? "bg-amber-500/15 text-amber-400" :
                                  "bg-muted text-muted-foreground"
                                }`} data-testid={`effect-magnitude-${r.iv}`}>{r.effect_size.magnitude}</span>
                              ) : r.effect_size?.reliable === true ? (
                                <span className="text-[10px] text-muted-foreground">-</span>
                              ) : "-"}
                            </td>
                            <td className="py-2 px-3 text-center">
                              {r.assumptions_passed ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 inline" /> : r.assumptions?.length > 0 ? <AlertTriangle className="w-3.5 h-3.5 text-amber-400 inline" /> : <span className="text-muted-foreground">-</span>}
                            </td>
                            <td className="py-2 px-3 text-center">
                              {r.passed_to_stage2 ? <span className="text-[10px] font-bold text-emerald-400" data-testid={`passed-stage2-${r.iv}`}>Yes</span> : <span className="text-[10px] text-muted-foreground">No</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {rcaResult.stage1.summary && <p className="text-xs text-muted-foreground mt-3" data-testid="screening-summary">{rcaResult.stage1.summary.message}</p>}
                  {rcaResult.stage1.validated_predictors?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className="text-[10px] text-muted-foreground">Validated for Stage 2:</span>
                      {rcaResult.stage1.validated_predictors.map((p, i) => (
                        <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-medium">{p}</span>
                      ))}
                    </div>
                  )}
                </>
              )}
            </CollapsibleSection>
          )}

          {rcaResult.stage2 && (
            <CollapsibleSection
              title={<><span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/15 text-primary mr-2">STAGE 2</span>Multivariate Model</>}
              subtitle={rcaResult.stage2.ran ? getStage2Narrative(rcaResult.stage2) : null}
              open={stage2Open} onToggle={() => setStage2Open(v => !v)} testId="rca-stage2"
            >
              {!rcaResult.stage2.ran ? (
                <p className="text-sm text-muted-foreground" data-testid="stage2-not-run">{rcaResult.stage2.reason}</p>
              ) : (
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-3 items-center">
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-primary/10 text-primary" data-testid="model-type-badge">{rcaResult.stage2.model_type}</span>
                    {rcaResult.stage2.summary?.r_squared != null && <span className="text-xs text-muted-foreground">R² = <span className="font-mono font-medium text-foreground">{rcaResult.stage2.summary.r_squared}</span></span>}
                    {rcaResult.stage2.summary?.adj_r_squared != null && <span className="text-xs text-muted-foreground">Adj R² = <span className="font-mono font-medium text-foreground">{rcaResult.stage2.summary.adj_r_squared}</span></span>}
                    {rcaResult.stage2.summary?.pseudo_r_squared != null && <span className="text-xs text-muted-foreground">Pseudo R² = <span className="font-mono font-medium text-foreground">{rcaResult.stage2.summary.pseudo_r_squared}</span></span>}
                    {rcaResult.stage2.summary?.aic != null && <span className="text-xs text-muted-foreground">AIC = <span className="font-mono text-foreground">{rcaResult.stage2.summary.aic}</span></span>}
                    <span className="text-xs text-muted-foreground">n = {rcaResult.stage2.n}</span>
                  </div>
                  {rcaResult.stage2.diagnostics?.breusch_pagan && (
                    <div className="flex flex-wrap gap-3 text-[10px]">
                      <span className={`px-2 py-0.5 rounded-full border ${rcaResult.stage2.diagnostics.breusch_pagan.heteroscedastic ? "bg-amber-500/10 border-amber-500/30 text-amber-400" : "bg-muted border-border text-muted-foreground"}`}>
                        Breusch-Pagan p={rcaResult.stage2.diagnostics.breusch_pagan.p_value}{rcaResult.stage2.diagnostics.breusch_pagan.heteroscedastic ? " (heteroscedastic)" : " (OK)"}
                      </span>
                      {rcaResult.stage2.diagnostics.cooks_distance && (
                        <span className={`px-2 py-0.5 rounded-full border ${rcaResult.stage2.diagnostics.cooks_distance.warning ? "bg-amber-500/10 border-amber-500/30 text-amber-400" : "bg-muted border-border text-muted-foreground"}`}>
                          Cook's D: {rcaResult.stage2.diagnostics.cooks_distance.influential_points} influential pts
                        </span>
                      )}
                    </div>
                  )}
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="multivariate-table">
                      <thead>
                        <tr className="border-b border-border text-muted-foreground">
                          <th className="text-center py-2 px-3 font-medium">Rank</th>
                          <th className="text-left py-2 px-3 font-medium">Predictor</th>
                          <th className="text-right py-2 px-3 font-medium">Coefficient</th>
                          <th className="text-right py-2 px-3 font-medium">Std Coef</th>
                          <th className="text-left py-2 px-3 font-medium">Direction</th>
                          <th className="text-right py-2 px-3 font-medium">p-value</th>
                          <th className="text-left py-2 px-3 font-medium">Effect</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rcaResult.stage2.predictors.map((p, i) => (
                          <tr key={i} className="border-b border-border/50">
                            <td className="py-2 px-3 text-center font-bold text-primary">#{p.rank}</td>
                            <td className="py-2 px-3 font-medium">{p.predictor}</td>
                            <td className="py-2 px-3 text-right font-mono">{p.coefficient}</td>
                            <td className="py-2 px-3 text-right font-mono font-medium">{p.standardized_coef}</td>
                            <td className="py-2 px-3">
                              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${p.direction === "positive" ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>{p.direction}</span>
                            </td>
                            <td className="py-2 px-3 text-right font-mono">{p.p_value != null ? p.p_value.toFixed(4) : "-"}</td>
                            <td className="py-2 px-3">
                              {p.effect_size?.reliable === false ? (
                                <span className="text-[10px] text-muted-foreground italic" title={p.effect_size?.reliability_reason}>Not reliable</span>
                              ) : p.effect_size?.magnitude ? (
                                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                                  p.effect_size.magnitude === "large" ? "bg-emerald-500/15 text-emerald-400" :
                                  p.effect_size.magnitude === "medium" ? "bg-amber-500/15 text-amber-400" :
                                  "bg-muted text-muted-foreground"
                                }`}>{p.effect_size.magnitude}</span>
                              ) : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {rcaResult.stage2.vif?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Multicollinearity (VIF)</p>
                      <div className="flex flex-wrap gap-2">
                        {rcaResult.stage2.vif.map((v, i) => (
                          <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full border ${
                            !v.kept ? "bg-red-500/10 border-red-500/30 text-red-400 line-through" :
                            v.vif > 3 ? "bg-amber-500/10 border-amber-500/30 text-amber-400" :
                            "bg-muted border-border text-muted-foreground"
                          }`}>{v.predictor}: {v.vif ?? "removed"}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CollapsibleSection>
          )}

          {rcaResult.confidence && (
            <CollapsibleSection
              title={<>Confidence: <span className={rcaResult.confidence.level === "High" ? "text-emerald-400" : rcaResult.confidence.level === "Moderate" ? "text-amber-400" : "text-red-400"}>{rcaResult.confidence.level}</span> <span className="text-xs font-mono text-muted-foreground ml-1">({rcaResult.confidence.score}/100)</span></>}
              icon={<Shield className="w-4 h-4 text-primary" />}
              open={confidenceOpen} onToggle={() => setConfidenceOpen(v => !v)} testId="confidence-score"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="flex-1 max-w-[200px] h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${rcaResult.confidence.score >= 80 ? "bg-emerald-500" : rcaResult.confidence.score >= 55 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${rcaResult.confidence.score}%` }} />
                </div>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5">
                {rcaResult.confidence.reasons?.map((r, i) => <span key={i} className="text-[10px] text-muted-foreground">{r}</span>)}
              </div>
              {rcaResult.confidence.penalties?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {rcaResult.confidence.penalties.map((p, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/20 text-red-400">{p.rule} ({p.delta})</span>
                  ))}
                </div>
              )}
            </CollapsibleSection>
          )}

          {rcaResult.final_drivers?.length > 0 && <FinalDriversPanel result={rcaResult} />}

          {rcaResult.guardrails?.action_warning ? (
            <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg" data-testid="action-warning">
              <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-red-400 font-medium">{rcaResult.guardrails.action_warning}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{rcaResult.guardrails?.causation_text || "Statistical association detected. Causation requires experimental validation."}</p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2 p-3 bg-muted/30 border border-border rounded-lg" data-testid="causation-disclaimer">
              <Info className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
              <p className="text-xs text-muted-foreground">{rcaResult.guardrails?.causation_text || "Statistical association detected. Causation requires experimental validation."}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
